import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../api/auth';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import {
  BellIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'clientSettings';

const Settings = () => {
  const { user, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');

  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    bookingReminders: true,
    promoNotifications: true,
    marketingEmails: false,
    profileVisible: true,
    shareDataForRecommendations: true,
    showLocationToProviders: true,
    language: 'English',
    currency: 'UGX',
    theme: 'system',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Sign-in method flags from /auth/me: hasPassword, googleLinked, verified
  const [signinInfo, setSigninInfo] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      setSettings(s => ({ ...s, ...saved }));
    } catch { /* ignore */ }
    authAPI.getCurrentUser().then(setSigninInfo).catch(() => {});
  }, []);

  const handleSave = () => {
    setSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      toast.success('Settings saved');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    const nextErrors = {};
    if (!passwordForm.currentPassword) nextErrors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) nextErrors.newPassword = 'New password is required';
    else if (passwordForm.newPassword.length < 8) {
      nextErrors.newPassword = 'New password must be at least 8 characters';
    }
    if (!passwordForm.confirmPassword) nextErrors.confirmPassword = 'Please confirm your new password';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.resetPassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordErrors({ form: error?.response?.data?.message || 'Failed to update password' });
    } finally {
      setSavingPassword(false);
    }
  };

  const updatePasswordField = (field, value) => {
    setPasswordForm(p => ({ ...p, [field]: value }));
    setPasswordErrors(p => ({ ...p, [field]: '', form: '' }));
  };

  // Google-only accounts create their first password — no current password needed
  const handleSetPassword = async () => {
    const nextErrors = {};
    if (!passwordForm.newPassword) nextErrors.newPassword = 'Password is required';
    else if (passwordForm.newPassword.length < 12) {
      nextErrors.newPassword = 'Password must be at least 12 characters';
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSavingPassword(true);
    try {
      await authAPI.setPassword(passwordForm.newPassword);
      toast.success('Password set — you can now sign in with email and password too');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSigninInfo((prev) => (prev ? { ...prev, hasPassword: true } : prev));
    } catch (error) {
      const details = error?.response?.data?.details?.fieldErrors?.newPassword?.[0];
      setPasswordErrors({ form: details || error?.response?.data?.message || 'Failed to set password' });
    } finally {
      setSavingPassword(false);
    }
  };

  const toggleStyle = (checked) => ({
    position: 'relative',
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: checked ? 'var(--orange-9)' : 'var(--gray-6)',
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.2s ease',
    flexShrink: 0,
  });

  const toggleDot = (checked) => ({
    position: 'absolute',
    top: '3px',
    left: checked ? '22px' : '3px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s ease',
  });

  const SettingRow = ({ title, desc, children, last = false }) => (
    <div
      className="flex items-center justify-between"
      style={{ padding: '14px 0', borderBottom: last ? 'none' : '1px solid var(--gray-4)' }}
    >
      <div style={{ maxWidth: '70%' }}>
        <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>{title}</p>
        <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '2px 0 0 0', textAlign: 'left' }}>{desc}</p>
      </div>
      {children}
    </div>
  );

  const Toggle = ({ value, onChange }) => (
    <button type="button" onClick={onChange} style={toggleStyle(value)}>
      <div style={toggleDot(value)} />
    </button>
  );

  const selectStyle = {
    padding: '6px 12px', borderRadius: '8px',
    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
  };

  const tabs = [
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'privacy', label: 'Privacy', icon: LockClosedIcon },
    { id: 'preferences', label: 'Preferences', icon: Cog6ToothIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'account', label: 'Account', icon: UserCircleIcon },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Settings</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Manage your notifications, privacy, and account preferences
          </p>
        </div>

        {/* Account badge */}
        <div
          className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
          style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)' }}
        >
          <ShieldCheckIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
            {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Client'} Account
            {user?.createdAt && ` — Member since ${new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
          </span>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div>
              <SettingRow title="Email notifications" desc="Booking confirmations, updates, and important alerts via email">
                <Toggle value={settings.emailNotifications} onChange={() => setSettings(s => ({ ...s, emailNotifications: !s.emailNotifications }))} />
              </SettingRow>
              <SettingRow title="SMS notifications" desc="Get text messages for urgent booking updates">
                <Toggle value={settings.smsNotifications} onChange={() => setSettings(s => ({ ...s, smsNotifications: !s.smsNotifications }))} />
              </SettingRow>
              <SettingRow title="Booking reminders" desc="Reminders before your upcoming appointments">
                <Toggle value={settings.bookingReminders} onChange={() => setSettings(s => ({ ...s, bookingReminders: !s.bookingReminders }))} />
              </SettingRow>
              <SettingRow title="Promotion alerts" desc="Be notified about deals and offers from providers you follow">
                <Toggle value={settings.promoNotifications} onChange={() => setSettings(s => ({ ...s, promoNotifications: !s.promoNotifications }))} />
              </SettingRow>
              <SettingRow title="Marketing emails" desc="Tips, news, and promotions from the platform" last>
                <Toggle value={settings.marketingEmails} onChange={() => setSettings(s => ({ ...s, marketingEmails: !s.marketingEmails }))} />
              </SettingRow>
            </div>
          )}

          {/* Privacy */}
          {activeTab === 'privacy' && (
            <div>
              <SettingRow title="Public profile" desc="Allow providers to view your profile when you book or review">
                <Toggle value={settings.profileVisible} onChange={() => setSettings(s => ({ ...s, profileVisible: !s.profileVisible }))} />
              </SettingRow>
              <SettingRow title="Personalized recommendations" desc="Use your booking history to suggest providers you may like">
                <Toggle value={settings.shareDataForRecommendations} onChange={() => setSettings(s => ({ ...s, shareDataForRecommendations: !s.shareDataForRecommendations }))} />
              </SettingRow>
              <SettingRow title="Share location with providers" desc="Helps providers offer accurate distance and travel info" last>
                <Toggle value={settings.showLocationToProviders} onChange={() => setSettings(s => ({ ...s, showLocationToProviders: !s.showLocationToProviders }))} />
              </SettingRow>
            </div>
          )}

          {/* Preferences */}
          {activeTab === 'preferences' && (
            <div>
              <SettingRow title="Language" desc="Choose your preferred language">
                <select
                  value={settings.language}
                  onChange={(e) => setSettings(s => ({ ...s, language: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="English">English</option>
                  <option value="Luganda">Luganda</option>
                  <option value="Swahili">Kiswahili</option>
                  <option value="French">French</option>
                </select>
              </SettingRow>
              <SettingRow title="Currency" desc="Display prices in your preferred currency">
                <select
                  value={settings.currency}
                  onChange={(e) => setSettings(s => ({ ...s, currency: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="UGX">UGX (USh)</option>
                  <option value="KES">KES (KSh)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </SettingRow>
              <SettingRow title="Theme" desc="Switch between light and dark appearance" last>
                <select
                  value={settings.theme}
                  onChange={(e) => setSettings(s => ({ ...s, theme: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="system">System default</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </SettingRow>
            </div>
          )}

          {/* Security */}
          {activeTab === 'security' && (
            <div className="space-y-5">
              {/* Sign-in methods */}
              {signinInfo && (
                <div style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '18px' }}>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 12px', textAlign: 'left' }}>
                    Sign-in Methods
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      {
                        label: 'Google',
                        active: signinInfo.googleLinked,
                        activeText: 'Linked',
                        inactiveText: 'Not linked — sign in with Google once to link it',
                      },
                      {
                        label: 'Email & Password',
                        active: signinInfo.hasPassword,
                        activeText: 'Password set',
                        inactiveText: 'No password yet — set one below',
                      },
                      {
                        label: 'Email verified',
                        active: !!signinInfo.verified,
                        activeText: 'Verified',
                        inactiveText: 'Sign in with Google once to verify your Gmail',
                      },
                    ].map(({ label, active, activeText, inactiveText }) => (
                      <div key={label} className="flex items-center justify-between" style={{ padding: '10px 12px', borderRadius: '10px', backgroundColor: 'var(--gray-2)' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--gray-12)' }}>{label}</span>
                        <span style={{
                          fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px',
                          backgroundColor: active ? '#dcfce7' : 'var(--gray-4)',
                          color: active ? '#15803d' : 'var(--gray-10)',
                        }}>
                          {active ? activeText : inactiveText}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px', textAlign: 'left' }}>
                  {signinInfo && !signinInfo.hasPassword ? 'Set Password' : 'Change Password'}
                </p>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px', textAlign: 'left' }}>
                  {signinInfo && !signinInfo.hasPassword
                    ? 'Add a password so you can also sign in without Google'
                    : 'Update your password to keep your account secure'}
                </p>

                <div className="space-y-4">
                  {(!signinInfo || signinInfo.hasPassword) && (
                  <Input
                    label="Current Password"
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={(e) => updatePasswordField('currentPassword', e.target.value)}
                    placeholder="Enter current password"
                    error={passwordErrors.currentPassword}
                    showPasswordToggle
                  />
                  )}
                  <Input
                    label="New Password"
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={(e) => updatePasswordField('newPassword', e.target.value)}
                    placeholder="At least 8 characters"
                    error={passwordErrors.newPassword}
                    showPasswordToggle
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => updatePasswordField('confirmPassword', e.target.value)}
                    placeholder="Re-enter new password"
                    error={passwordErrors.confirmPassword}
                    showPasswordToggle
                  />
                  {passwordErrors.form && (
                    <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '13px', margin: 0, textAlign: 'left' }}>
                      {passwordErrors.form}
                    </p>
                  )}
                </div>

                <div className="flex justify-end mt-5">
                  <Button
                    onClick={signinInfo && !signinInfo.hasPassword ? handleSetPassword : handlePasswordChange}
                    loading={savingPassword}
                  >
                    {signinInfo && !signinInfo.hasPassword ? 'Set Password' : 'Update Password'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Account */}
          {activeTab === 'account' && (
            <div>
              <SettingRow title="Email" desc={user?.email || '—'}>
                <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>Read-only</span>
              </SettingRow>
              <SettingRow title="Phone" desc={user?.phone || 'Not set'}>
                <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>From profile</span>
              </SettingRow>
              <SettingRow title="Log out" desc="Sign out of your account on this device" last>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to log out?')) logout();
                  }}
                >
                  Log Out
                </Button>
              </SettingRow>

              {/* Danger Zone */}
              <div
                className="rounded-xl p-5 mt-8"
                style={{ backgroundColor: 'var(--red-2, #fff5f5)', border: '1px solid var(--red-6, #fca5a5)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon style={{ width: '18px', height: '18px', color: 'var(--red-9, #e5484d)' }} />
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--red-9, #e5484d)', margin: 0, textAlign: 'left' }}>
                    Danger Zone
                  </h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 14px', textAlign: 'left' }}>
                  Irreversible actions — please proceed with caution
                </p>

                <div className="flex items-start justify-between gap-4">
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--red-9, #e5484d)', margin: 0, textAlign: 'left' }}>
                      Delete Account
                    </p>
                    <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '2px 0 0', textAlign: 'left' }}>
                      Permanently delete your account, bookings, reviews, and saved data.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      const confirmed = window.confirm(
                        'WARNING: This will permanently delete your account and all your data including bookings, reviews, and favorites.\n\nThis action CANNOT be undone.\n\nAre you sure you want to proceed?'
                      );
                      if (confirmed) {
                        const doubleConfirm = window.prompt('To confirm, type "DELETE" below:');
                        if (doubleConfirm === 'DELETE') {
                          toast.error('Account deletion is not yet available. Please contact support.');
                        } else if (doubleConfirm !== null) {
                          toast.error('Account deletion cancelled — text did not match');
                        }
                      }
                    }}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save button — only for tabs without their own action */}
        {activeTab !== 'security' && activeTab !== 'account' && (
          <div className="flex justify-end mt-6">
            <Button onClick={handleSave} loading={saving} size="large">
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
